// Where the product's Help points in the customer guide (docs/spec/guide.md
// §7, D-436). The guide is served by the API at /guide behind the same
// session as the product, so every link is a plain <a href>, never a router
// link: the SPA does not own /guide, and a router navigation there would
// render the SPA's own not-found screen.
//
// The guide decides the language and the page itself (spec guide §3.2): the
// link says which language the product is showing (`lng`) and, when this
// screen has a page, which one (`to`). A page the guide has not written lands
// on the reader's role section, never on a 404, so a stale entry here costs
// a less precise landing and nothing else. Every page named below exists in
// apps/guide/src/content/docs/en, which guide-links.test.ts holds.

/**
 * Routes and their guide pages, first match wins: more specific patterns
 * come before the general ones. A pattern is a pathname prefix of whole
 * segments; `$` stands for one segment of any value.
 */
export const GUIDE_PAGES: readonly (readonly [pattern: string, page: string])[] = [
  ["/floor/station/$/op/$", "floor/log-output"],
  ["/floor/station/$/waste", "floor/record-waste"],
  ["/floor/station/$/remnant", "floor/record-waste"],
  ["/floor/station", "floor/pick-station"],
  ["/floor/my-shift", "floor/my-shift"],
  ["/floor/tray", "floor/work-offline"],
  ["/floor/scan", "warehouse/scan-to-bin"],
  ["/floor/stock", "warehouse/find-stock"],
  ["/floor/lookup", "warehouse/find-stock"],
  ["/floor/receive", "warehouse/receive-transfer"],
  ["/floor/counts", "warehouse/count-bin"],
  ["/floor/ship", "warehouse/pick-and-ship"],
  ["/floor/quality/$/cut", "quality/guided-cut"],
  ["/floor/quality", "quality/run-inspection"],
  ["/floor", "floor"],
  ["/production/dispatch", "planning/dispatch-board"],
  ["/production/shifts", "plant/shift-scoreboard"],
  ["/production/conflicts", "plant/settle-conflict"],
  ["/production", "plant"],
  ["/waste", "plant/waste-sources"],
  ["/quality/quarantine", "quality/quarantine"],
  ["/quality/inspections", "quality/run-inspection"],
  ["/quality", "quality"],
  ["/inventory/transfers", "warehouse/receive-transfer"],
  ["/inventory/counts", "warehouse/count-bin"],
  ["/inventory", "warehouse"],
  ["/sales/customers", "sales/add-customer"],
  ["/sales/quotes", "sales/quote-to-order"],
  ["/sales/orders", "sales/quote-to-order"],
  ["/sales/approvals", "sales/quote-approval"],
  ["/sales/reviews", "sales/quote-approval"],
  ["/sales/pipeline", "sales/deals-and-tasks"],
  ["/sales/tasks", "sales/deals-and-tasks"],
  ["/sales/opportunities", "sales/deals-and-tasks"],
  ["/sales", "sales"],
  ["/messaging/settings", "admin/connect-telegram"],
  ["/messaging", "sales/reply-inbox"],
  ["/agents", "sales/offer-agent"],
  ["/planning/forecast", "planning/forecast"],
  ["/planning/forecasts", "planning/forecast"],
  ["/planning/mps", "planning/plan-the-week"],
  ["/planning/mrp", "planning/run-mrp"],
  ["/planning/settings/calendars", "planning/working-calendars"],
  ["/planning", "planning"],
  ["/catalogue", "admin/products-boms"],
  ["/products", "admin/products-boms"],
  ["/audit", "admin/audit-trail"],
  ["/settings/terminology", "admin/languages"],
  ["/onboarding", "admin/set-up-factory"],
  ["/home", "start/find-your-way"],
];

function matches(pattern: string, pathname: string): boolean {
  const want = pattern.split("/").filter((s) => s !== "");
  const have = pathname.split("/").filter((s) => s !== "");
  if (have.length < want.length) return false;
  return want.every((seg, i) => seg === "$" || seg === have[i]);
}

/** The guide page for a product pathname, or null when no page fits. */
export function guidePageFor(pathname: string): string | null {
  for (const [pattern, page] of GUIDE_PAGES) {
    if (matches(pattern, pathname)) return page;
  }
  return null;
}

/**
 * The Help link: `/guide/?lng=<product language>`, plus `&to=<page>` when the
 * screen has one. The language is the product's own code (`ru`, `zh-Hant`);
 * the guide maps it to its slug.
 */
export function guideHref(language: string, page?: string | null): string {
  const q = new URLSearchParams({ lng: language });
  if (page !== undefined && page !== null) q.set("to", page);
  return `/guide/?${q.toString()}`;
}
